import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin, Users, BedDouble, Bath, Star, Share2, ChevronLeft, BadgeCheck,
  X, CalendarCheck, ShieldCheck, Bed, Landmark, UtensilsCrossed, Waves, TrainFront, ShoppingBag, Trees, Languages, Phone, Mail, MessageCircle, Ruler, Sparkles, Compass, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { setMeta, setJsonLd, removeJsonLd, setCanonical, setOG } from "@/lib/seo";
import { useLang } from "@/context/LanguageContext";
import PublicLayout from "@/components/layout/PublicLayout";
import PropertyMap from "@/components/PropertyMap";
import { BookingBox, MobileBookingBar } from "@/components/StayBooking";
import { iconFor, groupSelectedAmenities } from "@/lib/amenities";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const CAT_ICONS = {
  attrazione: Landmark, ristorante: UtensilsCrossed, spiaggia: Waves,
  trasporti: TrainFront, negozi: ShoppingBag, natura: Trees,
};

export default function PropertyMiniSite() {
  const { t, lang } = useLang();
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [revData, setRevData] = useState(null);
  const [revLoading, setRevLoading] = useState(false);
  const [showOrig, setShowOrig] = useState(false);

  useEffect(() => { setRevData(null); setShowOrig(false); }, [lang, slug]);

  useEffect(() => {
    setLoading(true);
    api.get(`/public/properties/${slug}`)
      .then((r) => {
        setProperty(r.data);
        api.post("/public/track", { slug, type: "visit" }).catch(() => {});
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!property) return;
    const d = property;
    const trx = (d.translations && d.translations[lang]) || {};
    const seoL = (lang !== "it" && trx.seo) ? { ...(d.seo || {}), ...trx.seo } : (d.seo || {});
    const metaDesc = seoL.meta_description || trx.subtitle || d.subtitle || "";
    document.title = seoL.seo_title || `${d.name} | Quattro Accommodations`;
    setMeta("description", metaDesc);
    setCanonical(`${window.location.origin}/proprieta/${d.slug}`);
    const mainPhoto = (d.photos || []).find((p) => p.is_main) || (d.photos || [])[0];
    const ogImg = mainPhoto?.url || "";
    setOG("og:type", "website");
    setOG("og:site_name", "Quattro Accommodations");
    setOG("og:title", seoL.seo_title || d.name);
    setOG("og:description", metaDesc);
    setOG("og:image", ogImg);
    setOG("og:url", `${window.location.origin}/proprieta/${d.slug}`);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", seoL.seo_title || d.name);
    setMeta("twitter:description", metaDesc);
    setMeta("twitter:image", ogImg);
    const revs = d.reviews || [];
    const rating = revs.length ? (revs.reduce((a, x) => a + x.rating, 0) / revs.length).toFixed(2) : null;
    setJsonLd("ld-property", {
      "@context": "https://schema.org",
      "@type": "LodgingBusiness",
      "name": d.name,
      "description": metaDesc,
      "image": (d.photos || []).map((p) => p.url).slice(0, 6),
      "priceRange": `€${d.price_per_night}`,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": d.show_exact_location ? d.address : undefined,
        "addressLocality": d.city, "addressRegion": d.region,
        "postalCode": d.cap, "addressCountry": "IT",
      },
      "geo": { "@type": "GeoCoordinates", "latitude": d.lat, "longitude": d.lng },
      "amenityFeature": (d.amenities || []).map((a) => ({ "@type": "LocationFeatureSpecification", "name": a })),
      "aggregateRating": rating ? { "@type": "AggregateRating", "ratingValue": rating, "reviewCount": revs.length, "bestRating": "5" } : undefined,
    });
    return () => removeJsonLd("ld-property");
  }, [property, lang]);

  const share = () => {
    const origin = (typeof window !== "undefined" && window.location && window.location.origin) || process.env.REACT_APP_BACKEND_URL;
    const shareUrl = `${origin}/api/share/${slug}`;
    navigator.clipboard?.writeText(shareUrl);
    toast.success("Link copiato negli appunti!");
  };

  const translateReviews = async () => {
    setRevLoading(true);
    try {
      const { data } = await api.post(`/public/properties/${slug}/translate-reviews?lang=${lang}`);
      setRevData(data.reviews || []);
      setShowOrig(false);
    } catch {
      toast.error("Traduzione non riuscita, riprova");
    } finally {
      setRevLoading(false);
    }
  };

  if (loading) return <PublicLayout><div className="max-w-7xl mx-auto px-6 py-32 text-center text-brand-plum/50">Caricamento...</div></PublicLayout>;
  if (notFound || !property) return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-brand-plum mb-3">Casa non trovata</h1>
        <p className="text-brand-plum/60 mb-6">La struttura richiesta non è disponibile o non è pubblicata.</p>
        <Link to="/case-vacanze"><Button className="rounded-full bg-brand-plum text-white">Torna a case vacanze e B&B</Button></Link>
      </div>
    </PublicLayout>
  );

  const photos = [...(property.photos || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const main = photos.find((p) => p.is_main) || photos[0];
  const rest = photos.filter((p) => p.id !== main?.id).slice(0, 4);
  const owner = property.owner;
  const tr = (property.translations && property.translations[lang]) || {};
  const subtitleLoc = tr.subtitle || property.subtitle;
  const nearbyName = (n, i) => (tr.nearby_names && tr.nearby_names[i]) || n.name;
  const mapPoints = (property.nearby_places || [])
    .map((n, i) => ({ lat: n.lat, lng: n.lng, label: nearbyName(n, i), distance: n.distance }))
    .filter((p) => typeof p.lat === "number");
  const rating = property.reviews?.length ? (property.reviews.reduce((a, r) => a + r.rating, 0) / property.reviews.length).toFixed(2) : null;
  const loc = property.show_exact_location
    ? `${property.address}, ${property.city}`
    : `${property.neighborhood ? property.neighborhood + ", " : ""}${property.city}, ${property.region}`;

  const descBlocks = [
    [t("property.presentation"), tr.description?.presentation || property.description?.presentation],
    [t("property.spaces"), tr.description?.spaces || property.description?.spaces],
    [t("property.neighborhood"), tr.description?.neighborhood || property.description?.neighborhood],
    [t("property.other"), tr.description?.other || property.description?.other],
  ].filter(([, v]) => v);

  return (
    <PublicLayout>
      {/* mini-site top bar */}
      <div className="bg-brand-violet text-white/70 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
          <span className="font-mono flex items-center gap-1.5"><BadgeCheck size={13} className="text-brand-teal" /> {t("property.autonomous")} · quattro.house/proprieta/{property.slug}</span>
          <Link to="/case-vacanze" className="hidden sm:flex items-center gap-1 hover:text-brand-teal"><ChevronLeft size={13} /> {t("property.backToPortal")}</Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif text-brand-plum">{property.name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-1.5 text-sm text-brand-plum/60">
              {rating && <span className="flex items-center gap-1 font-semibold text-brand-plum"><Star size={14} className="fill-brand-gold text-brand-gold" /> {rating} · {property.reviews.length} {t("property.reviews")}</span>}
              <span className="flex items-center gap-1"><MapPin size={13} className="text-brand-teal" /> {property.city}, {property.region}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand-tealLight text-brand-tealDark" data-testid="property-type">{property.type}</span>
              {property.cin && <span className="text-xs px-2 py-0.5 rounded-full border border-brand-plum/15 text-brand-plum/60 font-mono" data-testid="property-cin">CIN {property.cin}</span>}
            </div>
          </div>
          <Button onClick={share} variant="ghost" data-testid="minisite-share-button" className="text-brand-plum hover:text-brand-teal shrink-0">
            <Share2 size={16} className="mr-1.5" /> {t("property.share")}
          </Button>
        </div>

        {/* Gallery bento */}
        <div className="grid grid-cols-4 grid-rows-2 gap-2 rounded-3xl overflow-hidden h-[300px] sm:h-[440px]" data-testid="property-gallery">
          <button onClick={() => setGalleryOpen(true)} className="col-span-4 sm:col-span-2 row-span-2 relative group">
            <img src={main?.url} alt={main?.alt || property.name} title={main?.title || undefined} className="w-full h-full object-cover group-hover:brightness-95 transition" />
          </button>
          {rest.map((p, i) => (
            <button key={p.id} onClick={() => setGalleryOpen(true)} className={`relative group hidden sm:block ${i >= 2 ? "" : ""}`}>
              <img src={p.url} alt={p.alt} title={p.title || undefined} className="w-full h-full object-cover group-hover:brightness-95 transition" />
              {i === rest.length - 1 && photos.length > 5 && (
                <span className="absolute inset-0 bg-black/40 flex items-center justify-center text-white font-medium">+{photos.length - 5} foto</span>
              )}
            </button>
          ))}
        </div>
        <button onClick={() => setGalleryOpen(true)} data-testid="show-all-photos" className="mt-3 text-sm text-brand-plum underline underline-offset-2 hover:text-brand-teal">
          {t("property.showAll")} ({photos.length})
        </button>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-52 lg:pb-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {/* spec bar + host */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-8 border-b border-brand-plum/10">
            <div>
              <h2 className="font-serif text-2xl text-brand-plum">{subtitleLoc}</h2>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-brand-plum/70">
                <span className="flex items-center gap-1.5"><Users size={16} className="text-brand-teal" /> {property.guests} ospiti</span>
                <span className="flex items-center gap-1.5"><BedDouble size={16} className="text-brand-teal" /> {property.bedrooms} camere</span>
                <span className="flex items-center gap-1.5"><Bed size={16} className="text-brand-teal" /> {property.beds} letti</span>
                <span className="flex items-center gap-1.5"><Bath size={16} className="text-brand-teal" /> {property.bathrooms} bagni</span>
                {property.sqm ? <span className="flex items-center gap-1.5" data-testid="property-sqm"><Ruler size={16} className="text-brand-teal" /> {property.sqm} m²</span> : null}
              </div>
            </div>
            {owner && (
              <div className="flex items-center gap-3" data-testid="host-block">
                <img src={owner.photo} alt={owner.first_name} className="w-14 h-14 rounded-full object-cover border-2 border-brand-teal" />
                <div>
                  <p className="text-xs text-brand-plum/50">{t("common.hostedBy")}</p>
                  <p className="font-serif text-lg text-brand-plum">{owner.first_name} {owner.last_name}</p>
                </div>
              </div>
            )}
          </div>

          {/* description */}
          <div className="space-y-6">
            {descBlocks.map(([title, text]) => (
              <div key={title}>
                <h3 className="font-serif text-xl text-brand-plum mb-2">{title}</h3>
                <p className="text-brand-plum/70 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          {/* amenities */}
          <div className="pt-8 border-t border-brand-plum/10">
            <h3 className="font-serif text-2xl text-brand-plum mb-5">{t("property.amenities")}</h3>
            <div className="space-y-6" data-testid="amenities-list">
              {groupSelectedAmenities(property.amenities || [], lang).map((group) => (
                <div key={group.id} data-testid={`amenities-cat-${group.id}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-plum/50 mb-3">{group.label}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {group.items.map((a) => {
                      const Icon = iconFor(a.icon);
                      return (
                        <div key={a.key} className="flex items-center gap-2.5 text-sm text-brand-plum/80">
                          <Icon size={18} className="text-brand-teal shrink-0" /> {lang === "en" ? (a.label_en || a.label) : a.label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* house rules */}
          {property.rules && (property.rules.checkin_from || property.rules.checkin_to || property.rules.checkout_by || property.rules.no_smoking || property.rules.no_pets || property.rules.self_checkin || property.rules.notify_host || property.rules.linen_change_extra || property.rules.cleaning_extra || property.rules.weekly_cleaning) && (
            <div className="pt-8 border-t border-brand-plum/10" data-testid="house-rules">
              <h3 className="font-serif text-2xl text-brand-plum mb-5">{t("property.rulesTitle")}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-brand-plum/80">
                {(property.rules.checkin_from || property.rules.checkin_to) && (
                  <div className="flex items-center gap-2.5"><CalendarCheck size={18} className="text-brand-teal shrink-0" /> {t("property.ruleCheckin")}: {t("property.ruleFrom")} {property.rules.checkin_from || "—"}{property.rules.checkin_to ? ` ${t("property.ruleTo")} ${property.rules.checkin_to}` : ""}</div>
                )}
                {property.rules.checkout_by && (
                  <div className="flex items-center gap-2.5"><CalendarCheck size={18} className="text-brand-teal shrink-0" /> {t("property.ruleCheckout")}: {t("property.ruleBy")} {property.rules.checkout_by}</div>
                )}
                {property.rules.self_checkin && <div className="flex items-center gap-2.5"><BadgeCheck size={18} className="text-brand-teal shrink-0" /> {t("property.ruleSelfCheckin")}</div>}
                {property.rules.no_smoking && <div className="flex items-center gap-2.5"><ShieldCheck size={18} className="text-brand-teal shrink-0" /> {t("property.ruleNoSmoking")}</div>}
                {property.rules.no_pets && <div className="flex items-center gap-2.5"><ShieldCheck size={18} className="text-brand-teal shrink-0" /> {t("property.ruleNoPets")}</div>}
                {property.rules.notify_host && <div className="flex items-center gap-2.5"><ShieldCheck size={18} className="text-brand-teal shrink-0" /> {t("property.ruleNotifyHost")}</div>}
                {property.rules.weekly_cleaning && <div className="flex items-center gap-2.5"><Sparkles size={18} className="text-brand-teal shrink-0" /> {t("property.ruleWeeklyCleaning")}</div>}
                {property.rules.linen_change_extra && <div className="flex items-center gap-2.5"><Sparkles size={18} className="text-brand-teal shrink-0" /> {t("property.ruleLinenExtra")}</div>}
                {property.rules.cleaning_extra && <div className="flex items-center gap-2.5"><Sparkles size={18} className="text-brand-teal shrink-0" /> {t("property.ruleCleaningExtra")}</div>}
              </div>
            </div>
          )}

          {/* owner card */}
          {owner && (
            <div className="pt-8 border-t border-brand-plum/10">
              <h3 className="font-serif text-2xl text-brand-plum mb-5">{t("property.yourHost")}</h3>
              <div className="rounded-2xl bg-brand-warm p-6 flex flex-col sm:flex-row gap-5 items-start">
                <img src={owner.photo} alt={owner.first_name} className="w-20 h-20 rounded-full object-cover border-2 border-brand-teal" />
                <div className="flex-1 w-full">
                  <p className="font-serif text-xl text-brand-plum">{owner.first_name} {owner.last_name}</p>
                  <p className="text-xs text-brand-tealDark font-medium mb-2">{t("property.verifiedHost")}</p>
                  {(owner.phone || owner.email) && (
                    <span data-testid="host-reachable-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-tealLight text-brand-tealDark mb-2">
                      <BadgeCheck size={13} /> {t("property.directReachable")}
                    </span>
                  )}
                  <p className="text-sm text-brand-plum/70 leading-relaxed">{owner.bio}</p>
                  {(owner.phone || owner.email) && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm" data-testid="host-contacts">
                      {owner.phone && <a href={`tel:${owner.phone}`} data-testid="host-phone" className="flex items-center gap-2 text-brand-tealDark hover:underline"><Phone size={15} /> {owner.phone}</a>}
                      {owner.email && <a href={`mailto:${owner.email}`} data-testid="host-email" className="flex items-center gap-2 text-brand-tealDark hover:underline"><Mail size={15} /> {owner.email}</a>}
                    </div>
                  )}
                  {owner.phone && (
                    <a
                      href={`https://wa.me/${(owner.phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(`${t("property.whatsappPrefill")} ${property.name} ${t("property.whatsappVia")}`)}`}
                      target="_blank" rel="noreferrer" data-testid="host-whatsapp-btn"
                      onClick={() => { api.post("/public/track", { slug, type: "whatsapp" }).catch(() => {}); }}
                      className="inline-flex items-center gap-2 mt-3 rounded-full bg-[#25D366] hover:bg-[#1eb457] text-white font-semibold text-sm px-4 h-10 transition-colors"
                    >
                      <MessageCircle size={16} /> {t("property.whatsappHost")}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* map */}
          <div className="pt-8 border-t border-brand-plum/10">
            <h3 className="font-serif text-2xl text-brand-plum mb-1">{t("property.whereTitle")}</h3>
            <p className="text-sm text-brand-plum/60 mb-5 flex items-center gap-1"><MapPin size={14} className="text-brand-teal" /> {loc}</p>
            <PropertyMap lat={property.lat} lng={property.lng} exact={property.show_exact_location} label={property.name} points={mapPoints} />
            {!property.show_exact_location && <p className="text-xs text-brand-plum/40 mt-2">{t("property.approxNote")}</p>}
          </div>

          {/* nearby / neighborhood guide */}
          {property.nearby_places?.length > 0 && (
            <div className="pt-8 border-t border-brand-plum/10">
              <h3 className="font-serif text-2xl text-brand-plum mb-5 flex items-center gap-2"><Landmark size={20} className="text-brand-teal" /> {t("property.nearbyTitle")}</h3>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="nearby-places-list">
                {property.nearby_places.map((n, i) => {
                  const Icon = CAT_ICONS[n.category] || MapPin;
                  return (
                    <div key={i} className="flex items-center gap-3 rounded-xl bg-white border border-brand-plum/10 p-3.5" data-testid={`nearby-place-${i}`}>
                      <span className="w-9 h-9 rounded-lg bg-brand-tealLight flex items-center justify-center shrink-0"><Icon size={17} className="text-brand-tealDark" /></span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-brand-plum truncate">{nearbyName(n, i)}</p>
                        <p className="text-xs text-brand-plum/50">{t(`cat.${n.category}`)}</p>
                      </div>
                      <span className="text-xs font-mono text-brand-teal shrink-0">{n.distance}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tours & experiences comparator (default) */}
          <div className="pt-8 border-t border-brand-plum/10">
            <a
              href="https://arovago.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="compare-tours-btn"
              onClick={() => { api.post("/public/track", { slug, type: "tours" }).catch(() => {}); }}
              className="group flex items-center justify-center gap-2.5 w-full rounded-full bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold h-14 px-6 transition-colors"
            >
              <Compass size={20} className="shrink-0" />
              {lang === "en" ? "Compare Tours & Experiences" : "Confronta Tour ed Esperienze"}
              <ArrowUpRight size={18} className="opacity-70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          {/* reviews */}
          {property.reviews?.length > 0 && (
            <div className="pt-8 border-t border-brand-plum/10">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h3 className="font-serif text-2xl text-brand-plum flex items-center gap-2">
                  <Star size={20} className="fill-brand-gold text-brand-gold" /> {rating} · {property.reviews.length} {t("property.reviews")}
                </h3>
                {lang !== "it" && (
                  revData ? (
                    <button onClick={() => setShowOrig((o) => !o)} data-testid="toggle-review-original"
                      className="text-sm font-medium text-brand-teal hover:text-brand-tealDark underline underline-offset-2">
                      {showOrig ? t("property.showTranslation") : t("property.showOriginal")}
                    </button>
                  ) : (
                    <Button onClick={translateReviews} disabled={revLoading} variant="outline" size="sm"
                      data-testid="translate-reviews-btn" className="border-brand-teal/40 text-brand-tealDark hover:bg-brand-tealLight">
                      <Languages size={15} className="mr-1.5" /> {revLoading ? t("property.translating") : t("property.translateReviews")}
                    </Button>
                  )
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                {property.reviews.map((r, i) => {
                  const useT = lang !== "it" && revData && !showOrig;
                  const comment = useT ? (revData[i]?.translated || r.comment) : r.comment;
                  return (
                    <div key={i} className="rounded-2xl bg-white border border-brand-plum/10 p-5" data-testid={`review-item-${i}`}>
                      <div className="flex items-center gap-1 mb-2">
                        {[...Array(5)].map((_, s) => <Star key={s} size={13} className="fill-brand-gold text-brand-gold" />)}
                      </div>
                      <p className="text-sm text-brand-plum/70 italic">"{comment}"</p>
                      <p className="text-xs text-brand-plum/50 mt-3 font-medium">{r.author}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Booking sidebar */}
        <div className="lg:col-span-1 hidden lg:block">
          <BookingBox property={property} />
        </div>
      </div>

      <MobileBookingBar property={property} />

      {/* Gallery modal */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-4xl p-0 bg-brand-violet border-0">
          <div className="max-h-[85vh] overflow-y-auto p-4 space-y-3">
            {photos.map((p) => (
              <img key={p.id} src={p.url} alt={p.alt} className="w-full rounded-xl object-cover" />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
